import React from 'react';
import {
  CONTROL_GROUP_ITEM_TARGET_DATA_ATTR,
  isUnrenderable,
  isValidElementOfType,
  ZINDEX_GROUP_INPUT_FOCUS,
} from '@tanium/coreui-utils';
import styled from '@tanium/react-emotion';
import { css } from '@tanium/style-system';
import { CONTROL_GROUP_ITEM_COREUITYPE, ControlGroupItemProps } from './ControlGroupItem';

const nativeControlSelectorList = 'button,input';
const dataAttrSelector = `[${CONTROL_GROUP_ITEM_TARGET_DATA_ATTR}]`;
// Selects explicitly marked elements with the data attr and native controls
// which are not inside an explicitly marked element.
const controlSelector = `:where(${dataAttrSelector}, :is(${nativeControlSelectorList}):not(${dataAttrSelector} :is(${nativeControlSelectorList})))`;
const ItemWrapper = styled('div')<ControlGroupItemProps>(({ grow }) =>
  css((theme) => ({
    display: 'flex',
    flexGrow: grow,
    marginRight: `-${theme.borderWidths[1]}px`,
    '&& > *': {
      flex: 'inherit',
    },
    [`&& ${controlSelector}`]: {
      borderRadius: 0,
    },
    'input:focus': {
      zIndex: ZINDEX_GROUP_INPUT_FOCUS,
    },
  })),
);

const ControlGroupContainer = styled('div')(
  css({
    display: 'flex',
    [`> ${ItemWrapper}:first-child`]: {
      [`& ${controlSelector}`]: {
        borderTopLeftRadius: 1,
        borderBottomLeftRadius: 1,
      },
    },
    [`> ${ItemWrapper}:last-child`]: {
      marginRight: 0,
      [`& ${controlSelector}`]: {
        borderTopRightRadius: 1,
        borderBottomRightRadius: 1,
      },
    },
  }),
);

const isControlGroupItemElement = (
  child: React.ReactNode,
): child is React.ReactElement<ControlGroupItemProps> =>
  isValidElementOfType(child, CONTROL_GROUP_ITEM_COREUITYPE);

const extractControlGroupItemProps = (child: React.ReactNode) => {
  if (!isControlGroupItemElement(child)) {
    return {} as Partial<ControlGroupItemProps>;
  }
  return child.props;
};

export interface Props {
  /**
   * Inputs and Buttons to be rendered as one unit
   */
  children?: React.ReactNode;
  /**
   * The `flex-grow` value to be applied to all Control Group Items. Defaults to `0`.
   */
  itemGrow?: number;
}

export const ControlGroup = ({ children, itemGrow = 0 }: Props) => (
  <ControlGroupContainer>
    {React.Children.map(children, (child) => {
      const { children: innerChildren = child, grow = itemGrow } =
        extractControlGroupItemProps(child);

      if (isUnrenderable(innerChildren)) {
        return null;
      }

      return <ItemWrapper grow={grow}>{innerChildren}</ItemWrapper>;
    })}
  </ControlGroupContainer>
);

ControlGroup.displayName = 'ControlGroup';
